import { Modal } from 'antd';
import { useState, type FC, type ReactNode } from 'react';
import { type Filter } from '../../../../services/question';
import { OwnTest } from './components/OwnTest';
import { SubjectsTest } from './components/SubjectTest';
import { TopicTest } from './components/TopicTest';
import { type ITest } from '../../../../types/test';
import { useUser } from '../../../../hooks/useUser';
import { OwnUserTest } from './components/OwnTest/OwnUserTest';
import { RatingTest } from './RatingTest/RatingTest';
import {
  IconBook,
  IconFileText,
  IconSparkles,
  IconStar,
  IconUserStar,
  IconTarget
} from '@tabler/icons-react';
import clsx from 'clsx';
import { useTheme } from '../../../../contexts/ThemeContext';
import './SelectTestModal.scss';

interface Props {
  onCancel: () => void
  onStart: (filter: Filter) => void
  visible: boolean
  onSelectTest: (test: ITest) => void
}

enum TestTypes {
  SUBJECT = 'subject',
  TOPIC = 'topic',
  SIMULATION = 'simulation',
  OWN = 'own',
  RATING = 'rating',
  MY_RATING = 'my-rating'
}

const SelectTestModal: FC<Props> = ({ onCancel, onStart, visible, onSelectTest }) => {
  const { isAdmin, isSuperAdmin } = useUser();
  const { theme } = useTheme();
  const [selectedSubject, setSelectedSubject] = useState<string>();
  const [selectedTopic, setSelectedTopic] = useState<string>();
  const [selectedTest, setSelectedTest] = useState<ITest>();
  const [rating, setRating] = useState(0);
  const [ownRating, setOwnRating] = useState(0);
  const [numberOfQuestions, setNumberOfQuestions] = useState(30);

  const [testType, setTestType] = useState<TestTypes>();

  const getDisabledState = () => {
    if (testType === TestTypes.TOPIC) {
      return !(selectedSubject && selectedTopic);
    }
    if (testType === TestTypes.SUBJECT) {
      return !selectedSubject;
    }
    if (testType === TestTypes.OWN) {
      return !selectedTest;
    }
    return false;
  };

  const handleStart = () => {
    if (testType === TestTypes.OWN) {
      if (selectedTest) {
        onSelectTest(selectedTest);
      }
    } else {
      onStart({
        rating,
        subjects: selectedSubject ? [selectedSubject!] : [],
        topics: selectedTopic ? [selectedTopic!] : [],
        ownRating,
        numberOfQuestions,
        onlyFailed: false
      });
    }
  };

  const getOwnTypeTest = (): ReactNode => {
    if (testType === TestTypes.OWN && (isAdmin || isSuperAdmin)) {
      return <OwnTest selectedTest={selectedTest} setSelectedTest={setSelectedTest} />;
    } else if (testType === TestTypes.OWN) {
      return <OwnUserTest selectedTest={selectedTest} setSelectedTest={setSelectedTest} />;
    } else {
      return null;
    }
  };

  const testTypeButtons = [
    {
      type: TestTypes.TOPIC,
      label: 'De un tema',
      description: 'Selecciona un tema específico para el test',
      icon: IconFileText,
      color: 'blue',
      onClick: () => setTestType(TestTypes.TOPIC)
    },
    {
      type: TestTypes.SUBJECT,
      label: 'De un bloque entero',
      description: 'Test con preguntas de todo un bloque',
      icon: IconBook,
      color: 'indigo',
      onClick: () => setTestType(TestTypes.SUBJECT)
    },
    {
      type: TestTypes.SIMULATION,
      label: 'Simulacro',
      description: 'Incluye preguntas de cualquier bloque',
      icon: IconTarget,
      color: 'purple',
      onClick: handleStart
    },
    {
      type: TestTypes.OWN,
      label: 'Tests propios',
      description: 'Tests que hemos diseñado para ti',
      icon: IconSparkles,
      color: 'pink',
      onClick: () => setTestType(TestTypes.OWN)
    },
    {
      type: TestTypes.RATING,
      label: 'Por dificultad (profesor)',
      description: 'Filtra por dificultad según criterio del profesor',
      icon: IconStar,
      color: 'amber',
      onClick: () => setTestType(TestTypes.RATING)
    },
    {
      type: TestTypes.MY_RATING,
      label: 'Por dificultad (propio)',
      description: 'Filtra por dificultad según tu criterio',
      icon: IconUserStar,
      color: 'orange',
      onClick: () => setTestType(TestTypes.MY_RATING)
    }
  ];

  return (
    <Modal
      title={
        <div className={clsx(
          'flex items-center gap-3',
          'text-gray-900 dark:text-white'
        )}>
          <div className={clsx(
            'w-10 h-10 rounded-lg flex items-center justify-center',
            'bg-gradient-to-br from-blue-500 to-purple-500',
            'dark:from-blue-600 dark:to-purple-600'
          )}>
            <IconTarget size={20} className="text-white" />
          </div>
          <span className="text-xl font-bold">Seleccionar test</span>
        </div>
      }
      open={visible}
      okButtonProps={{
        disabled: getDisabledState(),
        className: clsx(
          'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
          'border-none shadow-md hover:shadow-lg',
          'transition-all duration-300'
        )
      }}
      cancelButtonProps={{
        className: clsx(
          'border-gray-300 dark:border-gray-600',
          'text-gray-700 dark:text-gray-300',
          'hover:border-gray-400 dark:hover:border-gray-500',
          'transition-all duration-300'
        )
      }}
      onOk={handleStart}
      onCancel={onCancel}
      okText="Empezar"
      width={700}
      className={clsx(
        'select-test-modal',
        theme === 'dark' ? 'dark-modal' : 'light-modal'
      )}
      style={{
        borderRadius: '16px',
        overflow: 'hidden'
      }}
      maskStyle={{
        backdropFilter: 'blur(4px)',
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }}
      wrapClassName={clsx(
        'select-test-modal-wrapper',
        theme === 'dark' ? 'dark-modal-wrapper' : 'light-modal-wrapper',
        'bottom-sheet-mobile'
      )}
      centered={false}
    >
      {!testType && (
        <div className="space-y-4">
          <p className={clsx(
            'text-base font-medium mb-6 transition-colors duration-300',
            'text-gray-700 dark:text-gray-300',
            'text-center'
          )}>
            Selecciona el tipo de test que quieres hacer
          </p>
          <div className="grid grid-cols-1 gap-3">
            {testTypeButtons.map((button) => {
              const Icon = button.icon;

              const getColorClasses = () => {
                switch (button.color) {
                  case 'blue':
                    return {
                      bg: 'bg-blue-50 dark:bg-blue-900/20',
                      hover: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
                      border: 'border-blue-200 dark:border-blue-800',
                      icon: 'text-blue-600 dark:text-blue-400',
                      focus: 'focus:ring-blue-500 dark:focus:ring-blue-400'
                    };
                  case 'indigo':
                    return {
                      bg: 'bg-indigo-50 dark:bg-indigo-900/20',
                      hover: 'hover:bg-indigo-100 dark:hover:bg-indigo-900/30',
                      border: 'border-indigo-200 dark:border-indigo-800',
                      icon: 'text-indigo-600 dark:text-indigo-400',
                      focus: 'focus:ring-indigo-500 dark:focus:ring-indigo-400'
                    };
                  case 'purple':
                    return {
                      bg: 'bg-purple-50 dark:bg-purple-900/20',
                      hover: 'hover:bg-purple-100 dark:hover:bg-purple-900/30',
                      border: 'border-purple-200 dark:border-purple-800',
                      icon: 'text-purple-600 dark:text-purple-400',
                      focus: 'focus:ring-purple-500 dark:focus:ring-purple-400'
                    };
                  case 'pink':
                    return {
                      bg: 'bg-pink-50 dark:bg-pink-900/20',
                      hover: 'hover:bg-pink-100 dark:hover:bg-pink-900/30',
                      border: 'border-pink-200 dark:border-pink-800',
                      icon: 'text-pink-600 dark:text-pink-400',
                      focus: 'focus:ring-pink-500 dark:focus:ring-pink-400'
                    };
                  case 'amber':
                    return {
                      bg: 'bg-amber-50 dark:bg-amber-900/20',
                      hover: 'hover:bg-amber-100 dark:hover:bg-amber-900/30',
                      border: 'border-amber-200 dark:border-amber-800',
                      icon: 'text-amber-600 dark:text-amber-400',
                      focus: 'focus:ring-amber-500 dark:focus:ring-amber-400'
                    };
                  case 'orange':
                    return {
                      bg: 'bg-orange-50 dark:bg-orange-900/20',
                      hover: 'hover:bg-orange-100 dark:hover:bg-orange-900/30',
                      border: 'border-orange-200 dark:border-orange-800',
                      icon: 'text-orange-600 dark:text-orange-400',
                      focus: 'focus:ring-orange-500 dark:focus:ring-orange-400'
                    };
                  default:
                    return {
                      bg: 'bg-blue-50 dark:bg-blue-900/20',
                      hover: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
                      border: 'border-blue-200 dark:border-blue-800',
                      icon: 'text-blue-600 dark:text-blue-400',
                      focus: 'focus:ring-blue-500 dark:focus:ring-blue-400'
                    };
                }
              };

              const colors = getColorClasses();

              return (
                <button
                  key={button.type}
                  onClick={button.onClick}
                  className={clsx(
                    'group relative w-full p-4 rounded-xl border-2 transition-all duration-300',
                    'flex items-start gap-4 text-left',
                    colors.bg,
                    colors.border,
                    colors.hover,
                    'hover:shadow-md hover:scale-[1.02]',
                    'focus:outline-none focus:ring-2 focus:ring-offset-2',
                    colors.focus
                  )}
                >
                  {/* Icon */}
                  <div className={clsx(
                    'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center',
                    'bg-white dark:bg-surface-dark',
                    'group-hover:scale-110 transition-transform duration-300',
                    'shadow-sm'
                  )}>
                    <Icon
                      size={24}
                      className={colors.icon}
                      strokeWidth={2}
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className={clsx(
                      'font-semibold text-base mb-1 transition-colors duration-300',
                      'text-gray-900 dark:text-white'
                    )}>
                      {button.label}
                    </h3>
                    <p className={clsx(
                      'text-sm transition-colors duration-300',
                      'text-gray-600 dark:text-gray-400'
                    )}>
                      {button.description}
                    </p>
                  </div>

                  {/* Arrow indicator */}
                  <div className={clsx(
                    'flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300',
                    'text-gray-400 dark:text-gray-500'
                  )}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      className="group-hover:translate-x-1 transition-transform duration-300"
                    >
                      <path
                        d="M7.5 15L12.5 10L7.5 5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {getOwnTypeTest()}
      {testType === TestTypes.SUBJECT
        ? (
          <SubjectsTest
            selectedSubject={selectedSubject}
            setSelectedSubject={setSelectedSubject}
            setNumberOfQuestions={setNumberOfQuestions}
            numberOfQuestions={numberOfQuestions}
          />
        )
        : null}
      {testType === TestTypes.RATING
        ? (
          <RatingTest
            setRating={setRating}
            rating={rating}
            setNumberOfQuestions={setNumberOfQuestions}
            numberOfQuestions={numberOfQuestions}
          />
        )
        : null}
      {testType === TestTypes.MY_RATING
        ? (
          <RatingTest
            setRating={setOwnRating}
            rating={ownRating}
            setNumberOfQuestions={setNumberOfQuestions}
            numberOfQuestions={numberOfQuestions}
          />
        )
        : null}
      {testType === TestTypes.TOPIC
        ? (
          <TopicTest
            selectedSubject={selectedSubject}
            setSelectedSubject={setSelectedSubject}
            selectedTopic={selectedTopic}
            setSelectedTopic={setSelectedTopic}
            setNumberOfQuestions={setNumberOfQuestions}
            numberOfQuestions={numberOfQuestions}
          />
        )
        : null}
    </Modal>
  );
};

export { SelectTestModal };
